"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PerspectiveCameraControls = void 0;
const viewer_shared_services_1 = require("@shapediver/viewer.shared.services");
const gl_matrix_1 = require("gl-matrix");
const tsyringe_1 = require("tsyringe");
const __1 = require("../..");
const AbstractCameraControls_1 = require("./AbstractCameraControls");
const CameraControlsEventDistribution_1 = require("./perspective/CameraControlsEventDistribution");
const CameraControlsLogic_1 = require("./perspective/CameraControlsLogic");
class PerspectiveCameraControls extends AbstractCameraControls_1.AbstractCameraControls {
    // #endregion Properties (19)
    // #region Constructors (1)
    constructor(viewerId, camera, canvas, enabled) {
        super(viewerId, camera, canvas, enabled, __1.CAMERATYPE.PERSPECTIVE);
        // #region Properties (19)
        this._autoRotationSpeed = 0;
        this._cubePositionRestriction = { min: gl_matrix_1.vec3.fromValues(-Infinity, -Infinity, -Infinity), max: gl_matrix_1.vec3.fromValues(Infinity, Infinity, Infinity) };
        this._cubeTargetRestriction = { min: gl_matrix_1.vec3.fromValues(-Infinity, -Infinity, -Infinity), max: gl_matrix_1.vec3.fromValues(Infinity, Infinity, Infinity) };
        this._damping = 0.1;
        this._enableAutoRotation = false;
        this._enableKeyPan = false;
        this._enablePan = true;
        this._enableRotation = true;
        this._enableZoom = true;
        this._input = { keys: { up: 38, down: 40, left: 37, right: 39 }, mouse: { rotate: 0, zoom: 1, pan: 2 }, touch: { rotate: 1, zoom: 2, pan: 3 }, };
        this._keyPanSpeed = 0.5;
        this._movementSmoothness = 0.5;
        this._panSpeed = 0.5;
        this._rotationRestriction = { minPolarAngle: 0, maxPolarAngle: 180, minAzimuthAngle: -Infinity, maxAzimuthAngle: Infinity };
        this._rotationSpeed = 0.5;
        this._spherePositionRestriction = { center: gl_matrix_1.vec3.create(), radius: Infinity };
        this._sphereTargetRestriction = { center: gl_matrix_1.vec3.create(), radius: Infinity };
        this._zoomRestriction = { minDistance: 0, maxDistance: Infinity };
        this._zoomSpeed = 0.5;
        this._converter = tsyringe_1.container.resolve(viewer_shared_services_1.Converter);
        this._settingsEngine = tsyringe_1.container.resolve(viewer_shared_services_1.SettingsEngine);
        this._stateEngine = tsyringe_1.container.resolve(viewer_shared_services_1.StateEngine);
        this._cameraLogic = new CameraControlsLogic_1.CameraControlsLogic(this);
        this._cameraControlsEventDistribution = new CameraControlsEventDistribution_1.CameraControlsEventDistribution(this, this._cameraLogic);
    }
    applySettings() {
        const cameraSetting = this._settingsEngine.camera.cameras[this.camera.id];
        if (!cameraSetting)
            return;
        this.reset();
        const controlsSettings = cameraSetting.controls;
        this.autoRotationSpeed = controlsSettings.autoRotationSpeed;
        this.damping = controlsSettings.damping;
        this.enableAutoRotation = controlsSettings.enableAutoRotation;
        this.enableKeyPan = controlsSettings.enableKeyPan;
        this.enablePan = controlsSettings.enablePan;
        this.enableRotation = controlsSettings.enableRotation;
        this.enableZoom = controlsSettings.enableZoom;
        this.input = controlsSettings.input;
        this.keyPanSpeed = controlsSettings.keyPanSpeed;
        this.movementSmoothness = controlsSettings.movementSmoothness;
        this.rotationSpeed = controlsSettings.rotationSpeed;
        this.panSpeed = controlsSettings.panSpeed;
        this.zoomSpeed = controlsSettings.zoomSpeed;
        if (controlsSettings.restrictions.position.cube.min.x === null)
            controlsSettings.restrictions.position.cube.min.x = -Infinity;
        if (controlsSettings.restrictions.position.cube.min.y === null)
            controlsSettings.restrictions.position.cube.min.y = -Infinity;
        if (controlsSettings.restrictions.position.cube.min.z === null)
            controlsSettings.restrictions.position.cube.min.z = -Infinity;
        if (controlsSettings.restrictions.position.cube.max.x === null)
            controlsSettings.restrictions.position.cube.max.x = Infinity;
        if (controlsSettings.restrictions.position.cube.max.y === null)
            controlsSettings.restrictions.position.cube.max.y = Infinity;
        if (controlsSettings.restrictions.position.cube.max.z === null)
            controlsSettings.restrictions.position.cube.max.z = Infinity;
        if (controlsSettings.restrictions.position.sphere.radius === null)
            controlsSettings.restrictions.position.sphere.radius = Infinity;
        if (controlsSettings.restrictions.target.cube.min.x === null)
            controlsSettings.restrictions.target.cube.min.x = -Infinity;
        if (controlsSettings.restrictions.target.cube.min.y === null)
            controlsSettings.restrictions.target.cube.min.y = -Infinity;
        if (controlsSettings.restrictions.target.cube.min.z === null)
            controlsSettings.restrictions.target.cube.min.z = -Infinity;
        if (controlsSettings.restrictions.target.cube.max.x === null)
            controlsSettings.restrictions.target.cube.max.x = Infinity;
        if (controlsSettings.restrictions.target.cube.max.y === null)
            controlsSettings.restrictions.target.cube.max.y = Infinity;
        if (controlsSettings.restrictions.target.cube.max.z === null)
            controlsSettings.restrictions.target.cube.max.z = Infinity;
        if (controlsSettings.restrictions.target.sphere.radius === null)
            controlsSettings.restrictions.target.sphere.radius = Infinity;
        if (controlsSettings.restrictions.rotation.minAzimuthAngle === null)
            controlsSettings.restrictions.rotation.minAzimuthAngle = -Infinity;
        if (controlsSettings.restrictions.rotation.maxAzimuthAngle === null)
            controlsSettings.restrictions.rotation.maxAzimuthAngle = Infinity;
        if (controlsSettings.restrictions.zoom.maxDistance === null)
            controlsSettings.restrictions.zoom.maxDistance = Infinity;
        this.cubePositionRestriction = {
            min: this._converter.toVec3(controlsSettings.restrictions.position.cube.min),
            max: this._converter.toVec3(controlsSettings.restrictions.position.cube.max)
        };
        this.spherePositionRestriction = {
            center: this._converter.toVec3(controlsSettings.restrictions.position.sphere.center),
            radius: controlsSettings.restrictions.position.sphere.radius
        };
        this.cubeTargetRestriction = {
            min: this._converter.toVec3(controlsSettings.restrictions.target.cube.min),
            max: this._converter.toVec3(controlsSettings.restrictions.target.cube.max)
        };
        this.sphereTargetRestriction = {
            center: this._converter.toVec3(controlsSettings.restrictions.target.sphere.center),
            radius: controlsSettings.restrictions.target.sphere.radius
        };
        this.rotationRestriction = controlsSettings.restrictions.rotation;
        this.zoomRestriction = controlsSettings.restrictions.zoom;
    }
    // #endregion Constructors (1)
    // #region Public Accessors (38)
    get autoRotationSpeed() {
        return this._autoRotationSpeed;
    }
    set autoRotationSpeed(value) {
        this._autoRotationSpeed = value;
    }
    get cubePositionRestriction() {
        return this._cubePositionRestriction;
    }
    set cubePositionRestriction(value) {
        this._cubePositionRestriction = value;
    }
    get cubeTargetRestriction() {
        return this._cubeTargetRestriction;
    }
    set cubeTargetRestriction(value) {
        this._cubeTargetRestriction = value;
    }
    get damping() {
        return this._damping;
    }
    set damping(value) {
        this._damping = value;
    }
    get enableAutoRotation() {
        return this._enableAutoRotation;
    }
    set enableAutoRotation(value) {
        this._enableAutoRotation = value;
    }
    get enableKeyPan() {
        return this._enableKeyPan;
    }
    set enableKeyPan(value) {
        this._enableKeyPan = value;
    }
    get enablePan() {
        return this._enablePan;
    }
    set enablePan(value) {
        this._enablePan = value;
    }
    get enableRotation() {
        return this._enableRotation;
    }
    set enableRotation(value) {
        this._enableRotation = value;
    }
    get enableZoom() {
        return this._enableZoom;
    }
    set enableZoom(value) {
        this._enableZoom = value;
    }
    get input() {
        return this._input;
    }
    set input(value) {
        this._input = value;
    }
    get keyPanSpeed() {
        return this._keyPanSpeed;
    }
    set keyPanSpeed(value) {
        this._keyPanSpeed = value;
    }
    get movementSmoothness() {
        return this._movementSmoothness;
    }
    set movementSmoothness(value) {
        this._movementSmoothness = value;
    }
    get panSpeed() {
        return this._panSpeed;
    }
    set panSpeed(value) {
        this._panSpeed = value;
    }
    get rotationRestriction() {
        return this._rotationRestriction;
    }
    set rotationRestriction(value) {
        this._rotationRestriction = value;
    }
    get rotationSpeed() {
        return this._rotationSpeed;
    }
    set rotationSpeed(value) {
        this._rotationSpeed = value;
    }
    get spherePositionRestriction() {
        return this._spherePositionRestriction;
    }
    set spherePositionRestriction(value) {
        this._spherePositionRestriction = value;
    }
    get sphereTargetRestriction() {
        return this._sphereTargetRestriction;
    }
    set sphereTargetRestriction(value) {
        this._sphereTargetRestriction = value;
    }
    get zoomRestriction() {
        return this._zoomRestriction;
    }
    set zoomRestriction(value) {
        this._zoomRestriction = value;
    }
    get zoomSpeed() {
        return this._zoomSpeed;
    }
    set zoomSpeed(value) {
        this._zoomSpeed = value;
    }
}
exports.PerspectiveCameraControls = PerspectiveCameraControls;
//# sourceMappingURL=PerspectiveCameraControls.js.map